import { FC, useState } from 'react';
import { styled } from 'styled-components';
import { Table, TableColumnsType } from 'antd';
import { useSelector } from 'react-redux';
import { ITableRevenueData, rKeeperSelect } from '@entities/rKeeper';
import { textMixin } from '@styles/mixins';
import { colors } from '@styles';
import { PuffLoader } from 'react-spinners';
import { cssOverride } from '@utils/const';

const TableRevenueRestaurants: FC = () => {
    const [expandedRows, setExpandedRows] = useState<string[]>([]);
    const { revenueRestaurants, loading } = useSelector(rKeeperSelect);

    if (!revenueRestaurants) {
        return <PuffLoader color={colors.loader} cssOverride={cssOverride} />;
    }

    const columns: TableColumnsType<ITableRevenueData> = [
        {
            title: 'Рестораны',
            dataIndex: 'restaurantName',
            align: 'left',
            responsive: ['xl', 'lg', 'md', 'sm', 'xs'],
            render: (text: string, record) => (
                <span
                    style={{
                        fontWeight: record.children ? 600 : 400,
                    }}
                >
                    {text}
                </span>
            ),
        },
        Table.EXPAND_COLUMN,
        ...revenueRestaurants?.columns.map((column, index) => ({
            title: column,
            dataIndex: column,
            align: 'right',
            responsive: ['xl', 'lg', 'md', 'sm', 'xs'],

            render: (value: number) => (
                <StyledSum>
                    {new Intl.NumberFormat('ru-RU').format(value)} р.
                </StyledSum>
            ),
            key: `column-${index}`,
        })),
    ];

    const summary = () => {
        const totals: { [key: string]: string } | null =
            revenueRestaurants?.total;

        return (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                    <strong>Итого</strong>
                </Table.Summary.Cell>
                {revenueRestaurants?.columns.map((column, index) => (
                    <Table.Summary.Cell key={column} index={index + 1}>
                        <StyledSum style={{ fontWeight: 600 }}>
                            {new Intl.NumberFormat('ru-RU').format(
                                parseFloat(totals[column])
                            )}{' '}
                            р.
                        </StyledSum>
                    </Table.Summary.Cell>
                ))}
            </Table.Summary.Row>
        );
    };

    return (
        <StyledTable>
            <Table
                pagination={false}
                dataSource={revenueRestaurants?.table}
                columns={columns}
                scroll={{ x: 700 }}
                loading={loading === 'loading'}
                size="small"
                summary={summary}
                rowClassName={record =>
                    record.children ? 'parent-row' : 'child-row'
                }
                expandable={{
                    expandedRowKeys: expandedRows,
                    onExpand: (expanded, record) => {
                        setExpandedRows(
                            expanded
                                ? [...expandedRows, record.key]
                                : expandedRows.filter(key => key !== record.key)
                        );
                    },
                    rowExpandable: record => !!record.children,
                }}
            />
        </StyledTable>
    );
};

const StyledTable = styled.div`
    .ant-table-thead > tr > th {
        background-color: ${colors.tableHeadBg};
        border-radius: 0px !important;
        line-height: 16.8px;
        height: 50px;
        line-height: 17px;
        text-align: center;
        ${textMixin({
            fontSize: '14px',
            fontWeight: 600,
        })};
        height: 45px;
        padding-right: 1.1em !important;
    }

    .ant-table-summary {
        background-color: ${colors.tableHeadBg};
        ${textMixin({
            fontSize: '14px',
            fontWeight: 600,
        })};
        line-height: 16.8px;
        text-align: left;
    }
    tr > td:first-child {
        border-bottom-left-radius: 8px !important;
    }

    tr > td:last-child {
        border-bottom-right-radius: 8px !important;
    }

    .ant-table-row-indent {
        padding-left: 15px !important;
    }
    .indent-level-1 {
        padding-left: 22px !important;
    }

    table > tbody > tr > td {
        background-color: ${colors.cleanWhite};
        border: 1px solid #efefef;
        color: ${colors.grey};
        ${textMixin({
            fontSize: '14px',
            fontWeight: 400,
        })};
        line-height: 16.8px;
        text-align: left;
        height: 30px;
    }

    .parent-row td {
        font-weight: 600;
    }

    .child-row td {
        font-weight: 400;
    }

    .ant-table-expanded-row > td {
        padding-left: 20px !important;
        background-color: #f9f9f9;
    }
`;

const StyledSum = styled.div`
    text-align: right;
    padding-right: 0.5em;
    font-weight: 400;
`;

export default TableRevenueRestaurants;
